// Singleton dispatcher for the RGPD consent confirmation dialog.
// A host (mounted once at app root) subscribes via `subscribeGdprConsent`
// and resolves the pending request when the user confirms / cancels.

export type GdprConsentContext = "prospect_create" | "email_send" | "bulk_import";

export interface GdprConsentRequest {
  id: number;
  context: GdprConsentContext;
  resolve: (confirmed: boolean) => void;
}

type Listener = (req: GdprConsentRequest | null) => void;

let listener: Listener | null = null;
let nextId = 1;
let current: GdprConsentRequest | null = null;

const SESSION_PREFIX = "gdpr_consent_";

export function subscribeGdprConsent(fn: Listener): () => void {
  listener = fn;
  if (current) fn(current);
  return () => {
    if (listener === fn) listener = null;
  };
}

export function hasSessionConsent(context: GdprConsentContext): boolean {
  try {
    return sessionStorage.getItem(SESSION_PREFIX + context) === "1";
  } catch {
    return false;
  }
}

export function rememberSessionConsent(context: GdprConsentContext): void {
  try {
    sessionStorage.setItem(SESSION_PREFIX + context, "1");
  } catch {
    /* ignore */
  }
}

export function ensureGdprConsent(context: GdprConsentContext): Promise<boolean> {
  if (hasSessionConsent(context)) return Promise.resolve(true);

  if (current) {
    current.resolve(false);
    current = null;
  }

  return new Promise<boolean>((resolve) => {
    current = {
      id: nextId++,
      context,
      resolve: (v) => {
        current = null;
        listener?.(null);
        resolve(v);
      },
    };
    if (listener) {
      listener(current);
    } else {
      // No host mounted — fail-safe: cancel rather than silently allow.
      console.warn("[gdprConsent] No host mounted, cancelling action.");
      const c = current;
      current = null;
      resolve(false);
      void c;
    }
  });
}
